import { useMemo, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, RefreshCw } from 'lucide-react';
import { GoogleWalletBadge } from '@/shared/components/google-wallet-badge';
import { detectClientPlatform, isMacDesktop, type ClientPlatform } from '@/shared/lib/device';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  appleWalletCardUrl,
  fetchGoogleWalletSaveUrl,
  type PublicInvitePassbook,
} from '@/features/public-invite/api/use-public-invite';
import {
  APPLE_WALLET_APP_URL,
  openGoogleWalletApp,
  resolveCardDelivery,
  type CardDelivery,
} from '@/features/public-invite/model/card-delivery';

interface WalletCardActionsProps {
  passbook: PublicInvitePassbook;
  serialNumber: string;
  accentColor?: string;
  foregroundColor?: string;
  /** Para testes; no navegador vem do user agent. */
  platform?: ClientPlatform;
  /** Para testes: computador da Apple (opção direta = Carteira da Apple) ou não (= Google). */
  macDesktop?: boolean;
}

const secondaryLinkClass =
  'text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline';

function AppleWalletButton({ idPassbook, serialNumber, showOpen }: { idPassbook: string; serialNumber: string; showOpen: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <a
        href={appleWalletCardUrl(idPassbook, serialNumber)}
        className="block w-fit rounded-xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
      >
        <img src="/applewallet.svg" alt="Adicionar à Carteira da Apple" className="h-12 w-auto" />
      </a>
      {showOpen ? (
        <a href={APPLE_WALLET_APP_URL} className={secondaryLinkClass}>
          Já adicionou? Abrir a Carteira
        </a>
      ) : null}
    </div>
  );
}

function GoogleWalletButton({ idPassbook, serialNumber, showOpen }: { idPassbook: string; serialNumber: string; showOpen: boolean }) {
  // O link "Adicionar à Carteira do Google" é gerado na hora (JWT assinado): cacheado por serial.
  const { data: saveUrl, isPending, isError, refetch, isRefetching } = useQuery({
    queryKey: ['wallet-card', 'google', idPassbook, serialNumber],
    queryFn: () => fetchGoogleWalletSaveUrl(idPassbook, serialNumber),
    staleTime: 10 * 60_000,
  });

  if (isPending) return <Skeleton className="mx-auto h-12 w-52 rounded-xl" />;
  if (isError || !saveUrl) {
    return (
      <div className="flex flex-col items-center gap-2 text-sm">
        <p className="text-destructive">Não foi possível gerar o cartão agora.</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()} loading={isRefetching}>
          <RefreshCw className="size-4" />
          Tentar de novo
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3">
      <a
        href={saveUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Adicionar à Carteira do Google"
        className="block w-fit rounded-xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
      >
        <GoogleWalletBadge />
      </a>
      {showOpen ? (
        <button type="button" onClick={openGoogleWalletApp} className={secondaryLinkClass}>
          Já adicionou? Abrir a Carteira do Google
        </button>
      ) : null}
    </div>
  );
}

function DeliveryButton({
  delivery,
  passbook,
  serialNumber,
  accent,
  onPhone,
}: {
  delivery: CardDelivery;
  passbook: PublicInvitePassbook;
  serialNumber: string;
  accent?: CSSProperties;
  onPhone: boolean;
}) {
  if (delivery.kind === 'apple') {
    return <AppleWalletButton idPassbook={passbook.idPassbook} serialNumber={serialNumber} showOpen={onPhone} />;
  }
  if (delivery.kind === 'google') {
    return <GoogleWalletButton idPassbook={passbook.idPassbook} serialNumber={serialNumber} showOpen={onPhone} />;
  }
  if (delivery.kind === 'app') {
    return (
      <Button asChild size="lg" className={cn('w-full max-w-xs', accent && 'hover:opacity-90')} style={accent}>
        <a href={delivery.href} target="_blank" rel="noopener noreferrer">
          <Download className="size-4" />
          {delivery.label}
        </a>
      </Button>
    );
  }
  return null;
}

/**
 * Botão certo para pôr o cartão no celular, pela MESMA regra do cadastro
 * (`resolveCardDelivery`): Carteira da Apple no iPhone, Google Wallet no
 * Android, app da loja quando ela tem app próprio — sempre UMA opção, nunca as
 * duas. No computador: QR para continuar no celular e uma opção direta pela
 * conta (Apple no Mac, Google nos demais).
 */
export function WalletCardActions({
  passbook,
  serialNumber,
  accentColor,
  foregroundColor,
  platform: platformProp,
  macDesktop: macDesktopProp,
}: WalletCardActionsProps) {
  const platform = useMemo(() => platformProp ?? detectClientPlatform(), [platformProp]);
  const macDesktop = useMemo(() => macDesktopProp ?? isMacDesktop(), [macDesktopProp]);
  const accent: CSSProperties | undefined = accentColor
    ? { backgroundColor: accentColor, color: foregroundColor || '#fff' }
    : undefined;

  if (platform !== 'desktop') {
    const delivery = resolveCardDelivery(passbook, platform, serialNumber);
    return (
      <div className="space-y-3">
        <DeliveryButton delivery={delivery} passbook={passbook} serialNumber={serialNumber} accent={accent} onPhone />
        <p className="text-xs text-muted-foreground">
          {delivery.kind === 'apple'
            ? 'Toque no botão e depois em "Adicionar".'
            : delivery.kind === 'google'
              ? 'Toque no botão e salve o cartão na Carteira do Google.'
              : 'Baixe o app e entre com seu CPF para ver o cartão.'}
        </p>
      </div>
    );
  }

  const handoffUrl = `${window.location.origin}/clube/${passbook.idPassbook}?cartao=${encodeURIComponent(serialNumber)}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(handoffUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`Meu cartão ${passbook.organizationName}: ${handoffUrl}`)}`;
  const direct = resolveCardDelivery(passbook, macDesktop ? 'ios' : 'android', serialNumber);

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3">
        <p className="text-sm font-medium">Aponte a câmera do celular para o QR Code</p>
        <div className="w-fit rounded-2xl bg-white p-3 shadow-glass ring-1 ring-black/5">
          <img src={qrSrc} alt="QR Code para abrir o cartão no celular" width={200} height={200} className="rounded-xl" />
        </div>
        <p className="max-w-xs text-xs text-muted-foreground">
          No celular, o cartão abre com o botão certo para o seu aparelho.
        </p>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className={secondaryLinkClass}>
          Ou envie o link para o seu WhatsApp
        </a>
      </div>

      <div className="flex flex-col items-center gap-3 border-t border-border/60 pt-5">
        <p className="text-sm text-muted-foreground">
          {direct.kind === 'app'
            ? 'Ou baixe o app da loja:'
            : `Ou adicione direto pela sua conta ${macDesktop ? 'Apple' : 'Google'}:`}
        </p>
        <DeliveryButton delivery={direct} passbook={passbook} serialNumber={serialNumber} accent={accent} onPhone={false} />
      </div>
    </div>
  );
}
